import { BannerVariant, DismissedState, TaxContext } from '@/models/taxModels';

const STORAGE_VERSION = 1;
const MILESTONE_STEP = 500;

const storageKey = (affiliateId: number): string => `amba:w9-banner:${affiliateId}`;

const safeStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const emptyDismissed = (): DismissedState => ({
  version: STORAGE_VERSION,
  infoMilestone: null,
  warningDismissed: false,
});

export const loadDismissed = (affiliateId: number): DismissedState => {
  const storage = safeStorage();
  if (!storage) return emptyDismissed();
  try {
    const raw = storage.getItem(storageKey(affiliateId));
    if (!raw) return emptyDismissed();
    const parsed = JSON.parse(raw) as DismissedState;
    if (parsed.version !== STORAGE_VERSION) return emptyDismissed();
    return {
      version: STORAGE_VERSION,
      infoMilestone: typeof parsed.infoMilestone === 'number' ? parsed.infoMilestone : null,
      warningDismissed: parsed.warningDismissed === true,
    };
  } catch {
    return emptyDismissed();
  }
};

const writeDismissed = (affiliateId: number, value: DismissedState): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(storageKey(affiliateId), JSON.stringify(value));
  } catch {
    /* noop — storage full or disabled */
  }
};

export const setInfoDismissed = (affiliateId: number, milestone: number): void => {
  const current = loadDismissed(affiliateId);
  writeDismissed(affiliateId, { ...current, infoMilestone: milestone });
};

export const setWarningDismissed = (affiliateId: number): void => {
  const current = loadDismissed(affiliateId);
  writeDismissed(affiliateId, { ...current, warningDismissed: true });
};

export const currentMilestone = (earnings: number): number => Math.floor(earnings / MILESTONE_STEP) * MILESTONE_STEP;

export const parseDecimal = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const resolveBannerState = (ctx: TaxContext | null | undefined, dismissed: DismissedState): BannerVariant | null => {
  if (!ctx) return null;
  const settings = ctx.tax_settings;
  if (!settings?.enabled) return null;
  if (ctx.has_valid_w9) return null;

  if (settings.require_w9_before_approval) return 'blocked_onboarding';

  const earnings = parseDecimal(ctx.cash_earnings_ytd);
  const collection = parseDecimal(settings.collection_threshold);
  const reporting = parseDecimal(settings.reporting_threshold);

  if (reporting > 0 && earnings >= reporting) {
    return 'blocked_threshold';
  }

  if (collection > 0 && earnings >= collection && earnings < reporting) {
    if (dismissed.warningDismissed) return null;
    return 'warning';
  }

  if (earnings < collection) {
    const stored = dismissed.infoMilestone;
    if (stored !== null && currentMilestone(earnings) <= stored) return null;
    return 'info';
  }

  return null;
};
